import { useState, useEffect, useRef, forwardRef } from 'react';
import { inquiryType, contactType } from '@Components/Utilities/SandhillsConstants';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import { loadable } from '@Components/Utilities/SandhillsLoadable';
import TranslationText from '@Components/Utilities/TranslationText';
import translator from '@Components/Utilities/Translator';
import { sandhillsRequest, truncateString } from '@Components/Utilities/SandhillsUtility';
import LoadingRing from '@Components/MuiWrappers/LoadingRing';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import useKeyPress from '@Components/Utilities/KeyPress';
import Button from '@mui/material/Button';
import EventHeaderInfo from '@Components/Shared/EventHeaderInfo';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';

const DealerHeaderAccordion = loadable(() => import('@Components/Shared/DealerHeaderAccordion'));

const DealerHeaderInfo = forwardRef((props, ref) => {
    const routes = useRecoilValue(atoms.routes);
    const dealerLocation = {
        lat: props.GoogleAPIData?.PinLocation?.Latitude,
        lng: props.GoogleAPIData?.PinLocation?.Longitude
    };

    const windowSize = useWindowSize();
    const [openDescription, setOpenDescription] = useState(false);
    const [openMap, setOpenMap] = useState(false);
    const [buildMap, setBuildMap] = useState(true);
    const [biddingAgreementData, setBiddingAgreementData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const accordionRef = useRef(null);
    const currentWebsite = useRecoilValue(atoms.currentWebsite);

    useEffect(() => {
        focusMobileSelect(openMap);
    }, [openMap]);

    const focusMobileSelect = (focus) => {
        if (focus) {
            accordionRef.current?.focus();
        } else {
            accordionRef.current?.blur();
        }
    };

    const generateInfoWindow = (map) => {
        let tld = props.GoogleAPIData.TopLevelDomain;
        let hl = props.GoogleAPIData.TwoDigitISOLanguageCode;
        let viewOn = props.GoogleAPIData.ViewOnGoogleMaps;
        map.addListener('click', (e) => {
            if (e.placeId) {
                e.stop();
                let geocoder = new google.maps.Geocoder;
                geocoder.geocode({ 'placeId': e.placeId }, (results, status) => {
                    if (status === 'OK') {
                        if (results[0]) {
                            let newInfowindow = new google.maps.InfoWindow({
                                content: '<div class="google-maps-info-wrapper">' +
                                            '<div class="google-maps-info-content">' +
                                                '<div class="google-maps-info-address">' +
                                                    results[0].formatted_address +
                                                '</div>' +
                                                '<a href="https://www.google' + tld + '/maps/search/?api=1&hl=' + hl + '&query=' + results[0].formatted_address + '&query_place_id=' + results[0].place_id + '" target="_blank" class="view-link">' +
                                                viewOn + ' Google Maps</a>' +
                                            '</div>' +
                                        '</div>',
                                position: results[0].geometry.location
                            });
                            newInfowindow.open(map);
                        }
                    }
                });
            }
        });
    };

    const openEmailModal = (inquiry) => props.openEmailModal(inquiry || inquiryType.Email, contactType.Retail, {
        DealerGroupID: props.DealerGroupID,
        DealerCRMID: props.DealerCRMID,
        BranchName: props.BranchName,
        Subject: props.EmailSubject
    });

    props.ShowSendEmailButton && useKeyPress('Enter', openEmailModal, ref, inquiryType.Email);

    const toggleMap = () => {
        if (props.GoogleAPIData && buildMap && dealerLocation && dealerLocation.lat && dealerLocation.lng) {
            let map = new google.maps.Map(document.getElementById('dealer-map'), {
                zoom: 14,
                center: dealerLocation,
                disableDefaultUI: true,
                zoomControl: true,
                mapTypeControlOptions: {
                    mapTypeIds: [google.maps.MapTypeId.ROADMAP, google.maps.MapTypeId.HYBRID]
                }
            });

            let marker = new google.maps.Marker({ position: dealerLocation, map: map });
            let infowindow = new google.maps.InfoWindow({
                content: '<div class="google-maps-info-wrapper">' +
                            '<div class="google-maps-info-title">' +
                                props.Name +
                            '</div>' +
                            '<div class="google-maps-info-content">' +
                                '<div class="google-maps-info-address">' +
                                    (props.ShowStreetAddress ? props.Address1 + '<br />' : '') +
                                    props.LocationAddress +
                                '</div>' +
                                '<a href="' + props.GoogleAPIData.GoogleMapsExternalUrl + '" target="_blank" class="view-link">' +
                                props.GoogleAPIData.ViewOnGoogleMaps + ' Google Maps</a>' +
                            '</div>' +
                        '</div>'
            });

            marker.addListener('click', function () {
                infowindow.open(map, marker);
            });

            generateInfoWindow(map);
            setBuildMap(false);
        }

        setOpenMap(openMap => !openMap);
    };

    const hasDealerButtons = () => {
        return props.CompanyURL
            || props.IsLiveOrUpcomingEvent  
            || (props.OtherLocations && props.OtherLocations.length > 0);
    };

    const onBiddingAgreementDataUpdated = (biddingAgreementDataUpdated) => {
        setBiddingAgreementData(biddingAgreementDataUpdated);
        props.openBiddingAgreementModal?.({
            biddingAgreement: biddingAgreementDataUpdated,
            onBiddingAgreementDataUpdated: onBiddingAgreementDataUpdated
        });
    };

    const loadEventBiddingAggreement = () => {
        if (!isLoading) {
            if (biddingAgreementData) {
                props.openBiddingAgreementModal?.({
                    biddingAgreement: biddingAgreementData,
                    onBiddingAgreementDataUpdated: onBiddingAgreementDataUpdated
                });
            } else {
                setIsLoading(true);
                sandhillsRequest({
                    url: routes.GetBiddingAgreement.replace("{0}", props.EventID),
                    errorHandling: {
                        showDetails: true,
                        useConsole: false,
                        useAlert: true
                    }
                }).then((data) => {
                    if (data?.BidAgreement) {
                        setBiddingAgreementData(data);
                        setIsLoading(false);
                        props.openBiddingAgreementModal?.({
                            biddingAgreement: data,
                            onBiddingAgreementDataUpdated: onBiddingAgreementDataUpdated
                        });
                    } else {
                        alert('An error occurred while retrieving the Terms and Conditions.');
                    }
                }).catch((error) => {
                    setIsLoading(false);
                });
            }
        }
    };

    const hasContactInfoOnly = () => {
        return !(props.InventoryType || props.CurrencyCode) && !hasDealerButtons();
    };

    const showDealerAccordion = () => {
        return ((props.OtherLocations && props.OtherLocations.length > 0) || (props.isEvents && dealerLocation && dealerLocation.lat && dealerLocation.lng));
    };

    const getHeadArialabel = () => {
        if (props.EventTitle) {
            return translator.translate('sandhills.informationfor', [translator.translate('sandhills.tradepubwebsite.mvc.auction'), props.Name + " - " + props.EventTitle]);
        }
        else {
            return translator.translate('sandhills.informationfor', [translator.translate('sandhills.dealer'), props.Name]);
        }
    };

    const companyDescription = () => {
        const maxDescriptionLength = 300;

        return (
            <div className="dealer-header-desktop_description">
                {(props?.CompanyDescription?.length < maxDescriptionLength || openDescription ?
                    <span dangerouslySetInnerHTML={{ __html: props.CompanyDescription }}></span> :
                    <span dangerouslySetInnerHTML={{ __html: truncateString(props.CompanyDescription, maxDescriptionLength) }}></span>
                )}
                {props?.CompanyDescription?.length > maxDescriptionLength && <a className='dealer-header-desktop_description-button' role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); setOpenDescription(!openDescription); } }} onClick={() => setOpenDescription(!openDescription)}>{!openDescription ? <span>{translator.translate('sandhills.hostedstore.showmore')}</span> : <><br /><br /><span>{translator.translate('sandhills.showless')}</span></>}</a>}
            </div>
        );
    }

    const dealerDescription = () => {
        const maxDescriptionLength = 300;

        return (
            <div className="dealer-header-desktop_description">
                {(props?.Description?.length < maxDescriptionLength || openDescription ?
                    <span dangerouslySetInnerHTML={{ __html: props.Description }}></span> :
                    <span dangerouslySetInnerHTML={{ __html: truncateString(props.Description, maxDescriptionLength) }}></span>
                )}
                {props?.Description?.length > maxDescriptionLength && <a className='dealer-header-desktop_description-button' role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); setOpenDescription(!openDescription); } }} onClick={() => setOpenDescription(!openDescription)}>{!openDescription ? <span>{translator.translate('sandhills.hostedstore.showmore')}</span> : <><br /><br /><span>{translator.translate('sandhills.showless')}</span></>}</a>}
            </div>
        );
    }

    return (
        <>
            {props.EventID ? <> <div className='event-header'> <EventHeaderInfo loadEventBiddingAggreement={loadEventBiddingAggreement} {...props} /> </div>
                <br /></>:
                <div className={'dealer-header-info' + (props.EventTitle ? ' event-header-info' : '')}>
                    <div className='dealer-header-title bottom-border-separator'>
                        {props.EventTitle ?
                            <>
                                {windowSize.isMobile ?
                                    <div className="event-title-info">
                                        <div className="flex-between">
                                            <h2 aria-label={getHeadArialabel()}>{props.Name}</h2>
                                            <h5>{props.EventTitle}</h5>
                                        </div>
                                        <div className="flex-between">
                                            {props.StartDate && <span><b>{props.StartDate}</b></span>}
                                            {props.LocationAddress && <span>{props.LocationAddress}</span>}
                                        </div>
                                    </div>
                                    :
                                    <div className="event-title-info">
                                        <div className="flex-between">
                                            <h2 aria-label={getHeadArialabel()}>{props.Name}</h2>
                                            {props.StartDate && <span><b>{props.StartDate}</b></span>}
                                        </div>
                                        <div className="flex-between">
                                            <h5>{props.EventTitle}</h5>
                                            {props.LocationAddress && <span>{props.LocationAddress}</span>}
                                        </div>
                                    </div>}
                                {props.LogoUrl &&
                                    <div>
                                        <LazyLoadImage src={props.LogoUrl} className="dealer-header-title-logo" alt={props.Name ?? ''} />
                                        <br />
                                    </div>}
                            </>
                            :
                            <div className="dealer-title-info">
                                {props.LogoUrl &&
                                    <div>
                                        <LazyLoadImage src={props.LogoUrl} className="dealer-header-title-logo" alt={props.Name ?? ''} />
                                        <br />
                                        <br />
                                    </div>}
                                <h2 aria-label={getHeadArialabel()}>{props.Name}</h2>
                            </div>}
                        {props.InventoryManagementURL && <a href={props.InventoryManagementURL} target='_blank' aria-label={"View in Inventory Management (" + translator.translate('sandhills.opensinanewtab') + ")"}>
                            View in Inventory Management</a>}
                    </div>
                    {windowSize.isMobile ?
                        <div className='dealer-header-mobile'>
                            {props.CompanyDescription && companyDescription()}
                            {props.Description && dealerDescription()}
                            {(props.StartDate || props.EndDate || props.StartTime) ?
                                <div className='flex-column'>
                                    {props.StartDate && <span><b>{translator.translate('sandhills.startdate')}:</b>&nbsp;{props.StartDate}</span>}
                                    {props.EndDate && <span><b>{translator.translate('sandhills.enddate')}:</b>&nbsp;{props.EndDate}</span>}
                                    {props.StartTime && <span><b>{translator.translate('sandhills.time')}:</b>&nbsp;{props.StartTime}</span>}
                                </div>
                                :
                                <div className='dealer-header-contact-title'>
                                    <h3><TranslationText tKey='Sandhills.TradePubWebSite.MVC.ContactInfo' /></h3>
                                </div>}
                            <div className='flex-row'>      
                                {currentWebsite?.Site?.IsBidcaller ? <div className='contact-info'>{props.Name}</div> : <div></div>}                    
                                {props.EventTitle && <span><b>{translator.translate('sandhills.location')}:&nbsp;</b></span>}
                                {props.ShowStreetAddress && <div className='contact-info'>{props.Address1}</div>}
                                <div className='contact-info'>{props.LocationAddress}</div>
                                {(props.isEvents && props.WebsiteURL) &&
                                    <div className='contact-info'>
                                        <a id='dealer-header-website-link' href={props.WebsiteURL} target='_blank' aria-label={translator.translate('sandhills.tradepubwebsite.mvc.visitourwebsite') + " (" + translator.translate('sandhills.opensinanewtab') + ")"}>
                                            {translator.translate('sandhills.tradepubwebsite.mvc.visitourwebsite')}
                                        </a>
                                    </div>}
                            </div>
                            <span className='dealer-phone-mobile'>
                                {props.BusinessPhone &&
                                    <div>
                                        <span><strong><TranslationText tKey='sandhills.phone' />: </strong></span>
                                        <a id='dealer-header-phone-link' href={'tel:' + props.UnformattedBusinessPhone} className='dealer-phone'>
                                            {props.BusinessPhone}
                                        </a>
                                    </div>}
                                {props.ShowSendEmailButton ?
                                    <div className='dealer-contact-button-details' role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); openEmailModal(inquiryType.Email, ref.current['dealer-mobile-' + inquiryType.Email]); } }} ref={(element) => { ref.current['dealer-mobile-' + inquiryType.Email] = element }} onClick={() => openEmailModal(inquiryType.Email, ref.current['dealer-mobile-' + inquiryType.Email])}>
                                        <a id='dealer-header-send-email' className='send-email dealer-title-text'>
                                            <i className='fas fa-envelope' />
                                            <strong><TranslationText tKey='sandhills.sendemail' /></strong>
                                        </a>
                                    </div>
                                    :
                                    <div>
                                        {props.ContactName &&
                                            <span>
                                                <span><strong><TranslationText tKey='sandhills.contact' />: </strong></span>
                                                {props.ContactName}
                                            </span>}
                                    </div>}
                                {props.PremiumHostedSiteURL &&
                                    <div className='contact-info'>
                                        <a id='dealer-header-website-link' className='bold visit-our-website capitalize' href={props.PremiumHostedSiteURL} target='_blank' aria-label={translator.translate('sandhills.tradepubwebsite.mvc.visitourwebsite') + " (" + translator.translate('sandhills.opensinanewtab') + ")"}><strong>{translator.translate('sandhills.tradepubwebsite.mvc.visitourwebsite').toLowerCase()}</strong></a>
                                    </div>}
                            </span>
                            {(props.InventoryType || props.CurrencyCode) &&
                                <div className='flex-column'>
                                    {props.InventoryType && <span><b>{props.InventoryTypeLabel}:</b>&nbsp;{props.InventoryType}</span>}
                                    {props.CurrencyCode && <span><b>{translator.translate('sandhills.currency')}:</b>&nbsp;{props.CurrencyCode}</span>}
                                </div>}
                            {hasDealerButtons() &&
                                <div>
                                    {isLoading && <LoadingRing sxBox={{ 'justify-content': 'center' }} />}
                                    {(!props.EventTitle && !props.isEvents) && <h3>View Inventory</h3>}
                                    <div className='dealer-buttons'>
                                        {props.IsLiveOrUpcomingEvent &&
                                            <Button className='dealer-button' onClick={loadEventBiddingAggreement}>{translator.translate('sandhills.viewtermsandconditions')}</Button>}
                                        {props.CompanyURL &&
                                            <a className='dealer-button' href={props.CompanyURL}>{props.CompanyInventoryText}</a>}
                                        {props.OtherLocations && props.OtherLocations.length > 0 &&
                                            <a className='dealer-button' role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); toggleMap(); } }} onClick={toggleMap}>{props.LocationInventoryText}</a>}
                                    </div>
                                </div>}
                            {(props.AdditionalEventRings && props.AdditionalEventRings.length > 0) &&
                                <div className="flex-left-align event-rings">
                                    {props.AdditionalEventRings.map(item =>
                                        <a key={item.DisplayText} className={('dealer-button ts-button ' + (item.CssClass || '')).trim()} href={item.Url}><button className='ts-button'>{item.DisplayText}</button></a>)}
                                </div>
                            }
                        </div> :
                        <div className='dealer-header-container'>
                            <div className='dealer-header-desktop'>
                                <div className={'dealer-contact-info bottom-border-separator' + (hasContactInfoOnly() ? ' half-width' : ' flex-center-space-between')}>
                                    {(props.StartDate || props.EndDate || props.StartTime) ?
                                        <div className='flex-column'>
                                            {props.StartDate && <span><b>{translator.translate('sandhills.startdate')}:</b>&nbsp;{props.StartDate}</span>}
                                            {props.EndDate && <span><b>{translator.translate('sandhills.enddate')}:</b>&nbsp;{props.EndDate}</span>}
                                            {props.StartTime && <span><b>{translator.translate('sandhills.time')}:</b>&nbsp;{props.StartTime}</span>}
                                        </div>
                                        :
                                        <div className='dealer-header-contact-title'>
                                            <h3><TranslationText tKey='Sandhills.TradePubWebSite.MVC.ContactInfo' /></h3>
                                        </div>}
                                    <div className='flex-column'>
                                        {currentWebsite.Site.IsBidcaller ? <div className='contact-info'>{props.Name}</div> : <div></div>}    
                                        {props.EventTitle && <span><b>{translator.translate('sandhills.location')}:</b></span>}
                                        {props.ShowStreetAddress && <div className='contact-info'>{props.Address1}</div>}
                                        <div className='contact-info'>{props.LocationAddress}</div>
                                        {(props.isEvents && props.WebsiteURL) && <div className='contact-info'><a id='dealer-header-website-link' href={props.WebsiteURL} target='_blank' aria-label={translator.translate('sandhills.tradepubwebsite.mvc.visitourwebsite') + " (" + translator.translate('sandhills.opensinanewtab') + ")"}><TranslationText tKey='sandhills.tradepubwebsite.mvc.visitourwebsite' /></a></div>}
                                    </div>
                                    {props.BusinessPhone &&
                                        <div className='contact-info'>
                                            <span><strong><TranslationText tKey='sandhills.phone' />:&nbsp;</strong></span>
                                            <a href={'tel:' + props.UnformattedBusinessPhone} className='dealer-phone'>
                                                {props.BusinessPhone}
                                            </a>
                                        </div>}
                                    {props.ContactName &&
                                        <div className='contact-info'>
                                            <span><strong><TranslationText tKey='sandhills.contact' />:&nbsp;</strong></span>
                                            {props.ContactName}
                                        </div>}
                                    {props.PremiumHostedSiteURL &&
                                        <div className='contact-info'>
                                            <a id='dealer-header-website-link' className='bold visit-our-website capitalize' href={props.PremiumHostedSiteURL} target='_blank' aria-label={translator.translate('sandhills.tradepubwebsite.mvc.visitourwebsite') + " (" + translator.translate('sandhills.opensinanewtab') + ")"}><strong>{translator.translate('sandhills.tradepubwebsite.mvc.visitourwebsite').toLowerCase()}</strong></a>
                                        </div>}
                                    {props.ShowSendEmailButton &&
                                        <div className='dealer-contact-button-details' tabIndex="0" role="button" onClick={() => openEmailModal(inquiryType.Email, ref.current[inquiryType.Email])} ref={(element) => { ref.current[inquiryType.Email] = element }}>
                                            <a className='send-email dealer-title-text des-send-email'>
                                                <i className='fas fa-envelope' />
                                                <strong><TranslationText tKey='sandhills.sendemail' /></strong>
                                            </a>
                                        </div>}
                                    {props.Description && dealerDescription()}
                                </div>
                                <div className={hasContactInfoOnly() ? 'quarter-width' : 'flex-center-space-between'}>
                                    {(props.InventoryType || props.CurrencyCode) &&
                                        <div className='flex-column'>
                                            {props.InventoryType && <span><b>{props.InventoryTypeLabel}:</b>&nbsp;{props.InventoryType}</span>}
                                            {props.CurrencyCode && <span><b>{translator.translate('sandhills.currency')}:</b>&nbsp;{props.CurrencyCode}</span>}
                                        </div>}
                                    {isLoading && <LoadingRing sxBox={{ 'justify-content': 'center' }} />}
                                    {hasDealerButtons() &&
                                        <div className='dealer-buttons'>
                                            {props.IsLiveOrUpcomingEvent &&
                                                <Button className='dealer-button' onClick={loadEventBiddingAggreement}>{translator.translate('sandhills.viewtermsandconditions')}</Button>}
                                            {props.CompanyURL &&
                                                <a className='dealer-button' href={props.CompanyURL}>{props.CompanyInventoryText}</a>}
                                            {props.OtherLocations && props.OtherLocations.length > 0 &&
                                                <a className='dealer-button' role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); toggleMap(); } }} onClick={toggleMap}>{props.LocationInventoryText}</a>}
                                        </div>}
                                </div>
                            </div>
                            {props.CompanyDescription && companyDescription()}
                        </div>
                    }
                    {props.FuelPrices &&
                        <div className='fuel-prices'>
                            <h3>Our Fuel Prices</h3>
                            <div className='fuel-table'>
                                <div className='row fuel-heading'>
                                    <div className='column3 left'>Type</div>
                                    <div className='column2 left'>Brand</div>
                                    <div className='column2 left'>Price</div>
                                    <div className='column2 left'>Updated</div>
                                    <div className='column3 left'>Comments</div>
                                </div>
                                {props.FuelPrices.map((obj, i) =>
                                    <div className='row cf' key={i}>
                                        <div className='column3 left'>
                                            <div className='fuel-label'>Type: </div>
                                            {obj.Type}</div>
                                        <div className='column3 left'>
                                            <div className='fuel-label'>Brand: </div>
                                            {obj.Brand ? obj.Brand : <div>&nbsp;</div>}</div>
                                        <div className='column3 left margin-right'>
                                            <div className='fuel-label'>Price: </div>
                                            {obj.Price}</div>
                                        <div className='column3 left'>
                                            <div className='fuel-label'>Updated: </div>
                                            {obj.Updated}</div>
                                        <div className='column3 left'>
                                            <div className='fuel-label'>Comments: </div>
                                            {obj.Comments ? obj.Comments : <div>&nbsp;</div>}</div>
                                    </div>)}
                            </div>
                        </div>}
                    <div className='clear' />
                    {showDealerAccordion() &&
                        <DealerHeaderAccordion
                            {...props}
                            OpenMap={openMap}
                            ToggleMap={toggleMap}
                            ref={accordionRef}
                        />}
                </div>
            }
        </>
    );
});

export default DealerHeaderInfo;